/**
 * Report utility functions — formatting, CSV export, date helpers.
 * Shared across ModuleReports, ReportTable, and PDFGenerator.
 */
import { toast } from 'sonner';

export const fmtCurrency = (v) =>
  Number(v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export const fmtNum = (v) => Number(v || 0).toLocaleString('en-US');

/** Format number with currency code suffix, e.g. "1,234.56 (USD)" */
export const fmtCurrencyCode = (v, currency = 'USD') => {
  if (v === null || v === undefined || v === '') return '\u2014';
  const num = Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${num} (${currency})`;
};

/** Format discount as percentage, e.g. "0.00%" */
export const fmtPct = (v) => {
  if (v === null || v === undefined || v === '') return '\u2014';
  return `${Number(v || 0).toFixed(2)}%`;
};

/**
 * Format a date/time string in system timezone.
 * Shows: DD Mon YYYY, HH:MM  (no seconds)
 */
export const fmtDateTz = (d, tz) => {
  if (!d) return '\u2014';
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return String(d);
    return dt.toLocaleString('en-GB', {
      timeZone: tz || 'America/Los_Angeles',
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return String(d); }
};

/** Date-only format in system timezone (no time). */
export const fmtDateOnlyTz = (d, tz) => {
  if (!d) return '\u2014';
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return String(d);
    return dt.toLocaleDateString('en-GB', {
      timeZone: tz || 'America/Los_Angeles',
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return String(d); }
};

/** Detect if a column key is date-related */
export const isDateKey = (k) =>
  /date|created_at|updated_at|last_updated|received_at|approved_at|timestamp|period_date|disposal_date/.test(k);

export function exportCSV(rows, filename) {
  if (!rows.length) return;
  const skipKeys = new Set(['_rowType', '_isParent', '_isLineItem', '_parentRef', 'line_items']);
  const allKeys = new Set();
  rows.forEach(r => Object.keys(r).forEach(k => { if (!skipKeys.has(k)) allKeys.add(k); }));
  const keys = Array.from(allKeys);
  const csv = [keys.join(','), ...rows.map(r => keys.map(k => {
    let v = r[k];
    if (v === null || v === undefined) v = '';
    if (typeof v === 'object') v = JSON.stringify(v);
    if (typeof v === 'string' && (v.includes(',') || v.includes('"'))) v = `"${v.replace(/"/g, '""')}"`;
    return v;
  }).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url;
  a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
  toast.success(`Exported ${rows.length} rows to CSV`);
}
